import React from 'react';
import styles from './MedicalHistoryConfigItem.module.scss';
import Checkbox from '../../../../../../Components/Base/Checkbox/Checkbox';
import { Theme } from '../../../../../../Enums/Theme.enum';
import LinkButton from '../../../../../../Components/Button/LinkButton';
import RemoveButton from '../../../../../../Components/Button/RemoveButton';
import {
	MedicalHistoryType,
	useDeleteMedicalHistoryTypeMutation,
	useUpdateMedicalHistoryTypeMutation,
} from '../../../../../../graphql/graphql';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import useLoading from '../../../../../Base/Hooks/useLoading';
import Loading, { LoadingSize } from '../../../../../../Components/Loading';
import { useDisclose } from '../../../../../Modal/Hooks/useDisclose';
import MedicalHistoryConfigUpdateModal from './MedicalHistoryConfigUpdateModal';

interface Props {
	item: MedicalHistoryType;
	onChange: () => void;
}

const MedicalHistoryConfigItem: React.FC<Props> = ({ item, onChange }) => {
	const { isOpen, onOpen, onClose } = useDisclose();
	const { isLoading, setIsLoading } = useLoading();
	const [updateMedicalHistoryType] = useUpdateMedicalHistoryTypeMutation();
	const [deleteMedicalHistoryType] = useDeleteMedicalHistoryTypeMutation();
	const { displayError } = useApiClient();

	const onUpdate = async () => {
		onClose();
		onChange();
	};

	const handleToggleActive = async (active: boolean) => {
		setIsLoading(true);
		try {
			const response = await updateMedicalHistoryType({
				where: {
					id: item.id,
				},
				input: {
					active,
				},
			}).unwrap();

			if (response.updateMedicalHistoryType) {
				onChange();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const handleRemoveItem = async () => {
		try {
			const response = await deleteMedicalHistoryType({
				where: {
					id: item.id,
				},
			}).unwrap();

			if (response.deleteMedicalHistoryType) {
				onChange();
			}
		} catch (e) {
			displayError(e);
		}
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.toggle}>
				{isLoading ? (
					<Loading size={LoadingSize.SMALL} />
				) : (
					<Checkbox
						value={item.active}
						onChange={() => handleToggleActive(!item.active)}
					/>
				)}
			</div>
			<div className={styles.content}>
				<div className={styles.title}>{item.name}</div>
			</div>
			<div className={styles.controls}>
				<LinkButton
					title='base.shared.edit'
					theme={Theme.BLUE}
					firstUpper
					onClick={onOpen}
				/>
				<RemoveButton
					onClick={handleRemoveItem}
					title='base.shared.delete'
					firstUpper
				/>
			</div>
			{isOpen && (
				<MedicalHistoryConfigUpdateModal
					item={item}
					onUpdate={onUpdate}
					isOpen
					onClose={onClose}
				/>
			)}
		</div>
	);
};

export default MedicalHistoryConfigItem;
